import React, {useCallback, useState} from "react";
import {useRequest} from "../../../utils/hooks.jsx";
import {columns} from "./columns.jsx";
import {TableDateRangeTable} from "../../../components/OrdersDateRangeTable/TableDateRangeTable.jsx";
import {
    BidStatus,
    BidStatusToColorAdmin,
    briefPageSize,
    BriefStatus,
    BriefStatusToColorAdmin,
    BriefStatusToColorCompany,
    companyBriefProjection,
    companySelectedActions,
    tableColumns
} from "./consts.jsx";
import moment from "moment";
import "./company-briefs.css";
import {BriefBidsReceivedModal} from "../../../brief/BidsReceivedModal/BriefBidsReceivedModal.jsx";
import {BriefFormModal} from "../../../brief/BriefFormModal/BriefFormModal.jsx";
import {updateBriefStatus} from "../../../api/briefs/briefs.js";
import {updateBidStatus} from "../../../api/bids/bids.js";
import {message} from "antd";
import {useHistory} from "react-router";
import {isEmpty} from "lodash";
import {DeclineOrderModal} from "../../../event/DeclineOrderModal/DeclineOrderModal.jsx";
import {
    briefDeclineOptionsToText,
    BRIEF_DECLINE_MODAL_HEADER,
    BRIEF_DECLINE_MODAL_SUBTITLE
} from "../../../event/DeclineOrderModal/consts.js";

export const CompanyBriefs = () => {
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedBriefAction, setSelectedBriefAction] = useState(null);
    const [selectedActions, setSelectedAction] = useState(null);
    const [briefsDtstartOrder, setBriefsDtstartOrder] = useState(null);
    const [briefsCreatedAtOrder, setBriefsCreatedAtOrder] = useState(null);
    const [briefsSystemIdOrder, setBriefsSystemIdOrder] = useState([]);

    const [myBriefs, isLoadingBriefs, error, fetchBriefs] = useRequest(
        "/api/myBriefs",
        "POST",
        {
            page: currentPage,
            pageSize: briefPageSize,
            projection: companyBriefProjection,
            includeBids: true,
            ...(briefsDtstartOrder ? {orderBy: briefsDtstartOrder} : {}),
            ...(briefsCreatedAtOrder ? {orderBy: briefsCreatedAtOrder} : {}),
            ...(briefsSystemIdOrder?.length ? {systemIds: briefsSystemIdOrder} : {})
        },
        [currentPage, briefsDtstartOrder, briefsCreatedAtOrder, briefsSystemIdOrder],
        true,
        null,
        {}
    );

    const onCancelBrief = useCallback(brief => {
        setSelectedBriefAction(brief);
        setSelectedAction(companySelectedActions.canceledBrief);
    }, []);

    const closeActionModal = useCallback(() => {
        setSelectedAction(null);
        setSelectedBriefAction(null);
    }, []);

    const onDeclineBrief = useCallback(
        async declineDetails => {
            const res = await updateBriefStatus(
                selectedBriefAction?.briefId,
                BriefStatus.cancelled,
                new Date().getTime(),
                [],
                declineDetails.declineReason,
                declineDetails.declineComment
            );

            if (res?.error) {
                return;
            }

            fetchBriefs();
            closeActionModal();
        },
        [selectedBriefAction?.briefId, fetchBriefs, closeActionModal]
    );

    const onViewBriefClick = useCallback(brief => {
        setSelectedBriefAction({
            ...brief,
            date: moment(brief.dtstart),
            time: moment(brief.dtstart)
        });
        setSelectedAction(companySelectedActions.viewBrief);
    }, []);

    const onWatchBid = useCallback(
        async bid => {
            if (bid.status === BidStatus.pending) {
                const res = await updateBidStatus({
                    bidId: bid.bidId,
                    status: BidStatus.watched
                });

                if (res.error) {
                    message.error("שגיאה בעדכון סטטוס ההצעה");
                    return;
                }
            }

            history.push({pathname: `/bids/${bid.bidId}`, state: {bidId: bid.bidId}});
        },
        [history]
    );

    const onWatchBidCompany = useCallback(
        async bid => {
            history.push({pathname: `/bids/${bid.bidId}`, state: {bidId: bid.bidId}});
        },
        [history]
    );

    const onOpenBidsClick = useCallback(brief => {
        setSelectedBriefAction(brief);
        setSelectedAction(companySelectedActions.openOffers);
    }, []);

    return (
        <>
            <DeclineOrderModal
                visible={selectedActions === companySelectedActions.canceledBrief}
                onSave={onDeclineBrief}
                onClose={closeActionModal}
                header={BRIEF_DECLINE_MODAL_HEADER}
                subTitle={BRIEF_DECLINE_MODAL_SUBTITLE}
                declineReasons={briefDeclineOptionsToText}
                declineReasonLabel="סיבת ביטול"
                declineCommentLabel={null}
            />
            <BriefBidsReceivedModal
                visible={selectedActions === companySelectedActions.openOffers}
                onClose={closeActionModal}
                bids={selectedBriefAction?.bids?.filter(bid => bid.watchedByAdmin === true)}
                onBidClick={onWatchBid}
                actions={[
                    {
                        key: "view-bid",
                        title: "לצפייה בהצעת מחיר",
                        onClick: onWatchBidCompany
                    }
                ]}
                brief={selectedBriefAction}
                showReminderBtn={false}
            />

            <BriefFormModal
                visible={selectedActions === companySelectedActions.viewBrief}
                initValues={selectedBriefAction}
                onClose={closeActionModal}
                disable={true}
            />

            <TableDateRangeTable
                tableClassName="company-briefs"
                pagination={{
                    position: ["bottomRight"],
                    showSizeChanger: false,
                    current: currentPage,
                    defaultPageSize: briefPageSize,
                    total: myBriefs?.briefs?.length,
                    showTotal: total => <span>סה״כ {total}</span>,
                    onChange: page => setCurrentPage(page)
                }}
                isLoadingOrderTable={isLoadingBriefs}
                data={myBriefs?.briefs ?? []}
                showSearchByCreatedAt={false}
                showDateRangeFilter={false}
                onChange={(_, filters) => {
                    if (filters?.systemId !== undefined) {
                        setBriefsSystemIdOrder(filters.systemId || []);
                    }
                }}
                columns={columns({
                    currentPage,
                    briefStatusToColorMapper: BriefStatusToColorCompany,
                    onOpenBidsClick,
                    setBriefsDtstartOrder,
                    briefsDtstartOrder,
                    setBriefsCreatedAtOrder,
                    briefsCreatedAtOrder,
                    setBriefsSystemIdOrder,
                    briefsSystemIdOrder,
                    manageBriefs: [
                        {
                            shouldShow: brief =>
                                brief?.status != BriefStatus.cancelled &&
                                brief?.status != BriefStatus.accepted &&
                                brief?.bids?.length &&
                                brief?.bids?.some(bid => bid.watchedByAdmin && isEmpty(bid.note)),
                            text: "פתיחת הצעות",
                            onClick: brief => {
                                setSelectedBriefAction(brief);
                                setSelectedAction(companySelectedActions.openOffers);
                            }
                        }
                    ]
                })}
                hideColumns={[
                    tableColumns.companyName,
                    tableColumns.systemId,
                    tableColumns.comment,
                    tableColumns.assignedCSMs
                ]}
                actions={[
                    {
                        title: "צפייה בבריף",
                        onClick: onViewBriefClick
                    },
                    {
                        title: "צפייה בסטטוס הצעות",
                        onClick: brief => {
                            setSelectedBriefAction(brief);
                            setSelectedAction(companySelectedActions.openOffers);
                        }
                    },
                    {
                        title: "ביטול בריף",
                        needConfirmation: true,
                        className: "company-briefs-cancel-brief-btn",
                        onClick: onCancelBrief,
                        disabled: brief =>
                            brief.status === BriefStatus.cancelled || brief.status === BriefStatus.accepted
                    }
                ]}
                disableFutureDates={false}
            />
        </>
    );
};
